"use client";

import { useEffect, useState, useCallback, useRef } from "react";

type TocLevel = "h2" | "h3" | "h4";

interface TocItem {
  id: string;
  label: string;
  level: TocLevel;
}

const MOBILE_BREAKPOINT = 1024;

function buildSlug(text: string, index: number, used: Record<string, number>) {
  let base = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 45);
  if (!base) base = `h${index}`;

  let slug = base;
  let n = 1;
  while (used[slug]) {
    slug = `${base}-${n}`;
    n += 1;
  }
  used[slug] = 1;
  return slug;
}

function TocNav({
  items,
  activeId,
  onNavigate,
}: {
  items: TocItem[];
  activeId: string;
  onNavigate: (id: string) => void;
}) {
  return (
    <nav id="splitTocNav" aria-label="Page headings">
      {items.map((item, index) => {
        const showDivider =
          item.level === "h2" && index > 0 && items.slice(0, index).some((i) => i.level === "h2");
        return (
          <span key={item.id}>
            {showDivider && <div className="stoc-div" />}
            <a
              href={`#${item.id}`}
              className={`stoc-link stoc-${item.level}${activeId === item.id ? " stoc-active" : ""}`}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(item.id);
              }}
            >
              {item.label}
            </a>
          </span>
        );
      })}
    </nav>
  );
}

export function PillarTableOfContents() {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const manualTocScrollUntilRef = useRef(0);

  useEffect(() => {
    const col = document.querySelector(".pillar-page .split-content");
    if (!col) return;

    const all = col.querySelectorAll("h2,h3,h4");
    const heads: Element[] = [];

    all.forEach((el) => {
      let skip = false;
      let parent: Element | null = el.parentElement;
      while (parent && parent !== col) {
        const cls = parent.className;
        if (
          typeof cls === "string" &&
          (cls.includes("overlay") || cls.includes("ov-panel") || cls.includes("ov-wrap"))
        ) {
          skip = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (!skip) heads.push(el);
    });

    const used: Record<string, number> = {};
    const tocItems: TocItem[] = [];

    heads.forEach((heading, index) => {
      const text = (heading.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return;

      const slug = buildSlug(text, index, used);
      if (!heading.id) heading.id = slug;

      tocItems.push({
        id: slug,
        label: text.length > 62 ? `${text.slice(0, 59)}...` : text,
        level: heading.tagName.toLowerCase() as TocLevel,
      });
    });

    setItems(tocItems);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = visible[0]?.target?.id;
        if (id) setActiveId(id);
      },
      { rootMargin: "-90px 0px -55% 0px", threshold: 0 }
    );

    items.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    if (!popupOpen || !isMobile) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPopupOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [popupOpen, isMobile]);

  useEffect(() => {
    const tocInner = document.querySelector<HTMLElement>(".pillar-page .stoc-inner");
    if (!tocInner) return;

    const markManualScroll = () => {
      manualTocScrollUntilRef.current = Date.now() + 1200;
    };

    tocInner.addEventListener("wheel", markManualScroll, { passive: true });
    tocInner.addEventListener("touchmove", markManualScroll, { passive: true });

    return () => {
      tocInner.removeEventListener("wheel", markManualScroll);
      tocInner.removeEventListener("touchmove", markManualScroll);
    };
  }, []);

  useEffect(() => {
    if (!activeId) return;
    if (isMobile && !popupOpen) return;
    if (Date.now() < manualTocScrollUntilRef.current) return;

    const frame = window.requestAnimationFrame(() => {
      const tocInner = document.querySelector<HTMLElement>(".pillar-page .stoc-inner");
      const activeLink = document.querySelector<HTMLAnchorElement>(".pillar-page .stoc-inner .stoc-link.stoc-active");
      if (!tocInner || !activeLink) return;

      const containerRect = tocInner.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      const isVisibleEnough =
        linkRect.top >= containerRect.top + 12 && linkRect.bottom <= containerRect.bottom - 12;
      if (isVisibleEnough) return;

      // Keep the active TOC item centered only when it's out of view.
      const targetScrollTop =
        activeLink.offsetTop - tocInner.clientHeight / 2 + activeLink.clientHeight / 2;
      tocInner.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeId, isMobile, popupOpen]);

  const closePopup = useCallback(() => setPopupOpen(false), []);

  const handleNavigate = useCallback(
    (id: string) => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (isMobile) setPopupOpen(false);
    },
    [isMobile]
  );

  return (
    <>
      {isMobile && !popupOpen && (
        <button
          type="button"
          className="toc-fab"
          aria-label="Open table of contents"
          onClick={() => setPopupOpen(true)}
        >
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="15" y2="12" />
            <line x1="3" y1="18" x2="10" y2="18" />
          </svg>
          <span className="toc-fab-label">Contents</span>
        </button>
      )}

      {isMobile && (
        <div
          className={`toc-overlay${popupOpen ? " open" : ""}`}
          onClick={closePopup}
          aria-hidden={!popupOpen}
        />
      )}

      <aside
        className={`split-toc${isMobile && popupOpen ? " toc-open" : ""}`}
        id="splitToc"
        aria-label="Table of contents"
        aria-hidden={isMobile && !popupOpen}
      >
        <div className="stoc-inner">
          {isMobile && popupOpen && (
            <button
              type="button"
              className="toc-popup-close"
              aria-label="Close table of contents"
              onClick={closePopup}
            >
              ✕
            </button>
          )}
          <p className="stoc-title">Contents</p>
          <TocNav items={items} activeId={activeId} onNavigate={handleNavigate} />
        </div>
      </aside>
    </>
  );
}
